import React, { useState, useMemo, useRef, useEffect } from 'react'
import { 
  set as setTime, 
  add as addTime, 
  isAfter,
  format, 
  differenceInMinutes, 
  isValid, 
  parseISO,
  setHours,
  setMinutes
} from 'date-fns'
import { Clock } from 'react-feather'


function getTimeDiff(d1, d2) {
  const diff = differenceInMinutes(d1, d2)

  if (diff < 60) {
    return `${diff} mins`
  }

  return `${diff/60} hours`
}

export const mergeTime = (day, time) => {
  return setTime(
    day, 
    {
      hours: time.getHours(),
      minutes: time.getMinutes(),
      seconds: 0,
      milliseconds: 0,
    }
  )
}

export function isStartTimeAfterEndTime(start, end) {
  return (
    !isAfter(end, start) && (
      start.getHours() > end.getHours() ||
      (start.getHours() === end.getHours() && start.getMinutes() >= end.getMinutes())
    )
  )
}

export default function TimePicker(props) {
  let {
    value,
    onChange = () => {},
    interval = 15,
    minTime,
    maxTime,
    className,
    error,
  } = props

  const ref = useRef()
  const [isFocused, setIsFocused] = useState(false)

  const [inputTime, setInputTime] = useState()

  const times = useMemo(() => {
    // block if the value isn't a timestamp (if a user can put anything)
    if (!value || !isValid(value)) {
      return
    }

    let date = minTime
      ? addTime(minTime, { minutes: interval })
      : setTime(value, { hours: 0, minutes: 0 })

    let maxDate = maxTime
      ? maxTime
      : addTime(minTime || date, { hours: 23, minutes: 60 - interval })

    // maxDate ends the FOLLOWING day
    if (maxTime && isAfter(maxDate, date)) {
      maxDate = setTime(maxDate, {minutes: 0, hours: 5})
    }
      
    let vals = [date]

    while (date < maxDate) {
      date = addTime(date, { minutes: interval })
      vals.push(date)
    }

    return vals.map(date => {
      const val = {
        date,
        label: format(date, 'h:mma').toLowerCase(),
      }

      if (minTime) {
        val.difference = getTimeDiff(date, minTime)
      }

      return val
    })
  }, [value, minTime, interval])

  const currentTime = useMemo(() => {
    const result = times?.find(t => (
      t.date.getHours() === value.getHours() &&
      t.date.getMinutes() === value.getMinutes()
    ))
    if (result) {
      return result
    } else if (value && isValid(value)) {
      return { date: value, label: format(value, 'h:mma').toLowerCase() }
    } else {
      return null
    }
  }, [times, value])

  const [hoveredTime, setHoveredTime] = useState(currentTime)

  useEffect(() => {
    if (!isFocused || !currentTime) {
      return
    }

    const target = document.querySelector(`#time-${currentTime.date.getTime()}`)

    if (target) {
      target.parentNode.scrollTop = target.offsetTop
    }
  }, [isFocused])

  return (
    <div className={className}>
      <div className='relative flex items-center'>
        <Clock
          size={20}
          className='absolute left-0 mr-2 text-blue pointer-events-none'
        />
        <input
          ref={ref}
          className="flex-1 pl-8 block max-w-full text-md font-hairline"
          value={inputTime ? inputTime : currentTime ? currentTime.label : ''}
          onChange={(event) => {
            setInputTime(event.target.value)
          }}
          error={error}
          onFocus={() => {
            setIsFocused(true)
          }}
          onBlur={(e) => {
            if (!inputTime) { 
              return setIsFocused(false)
            }

            const cleaned = String(event.target.value).replace(/\s/g, '').toLowerCase()
            const matches = cleaned ? cleaned.match(/^([0-1]?[0-9]|2[0-3])(?::)?(?:([0-5][0-9]))?(a|p)?/) : null

            if (matches) {             
              const prevTime = value || new Date()

              let hours = Number(matches[1])
              let meridien = matches[3] || (currentTime.date.getHours() >= 12 ? 'p' : 'a')
              let minutes = matches[2] ? Number(matches[2]) : 0

              if (meridien === 'p' && hours < 12) {
                hours += 12
              }

              let newTime = setTime(prevTime, { hours, minutes })
              onChange(newTime)
            } 

            setIsFocused(false)
            setInputTime(null)
          }}
        />
        {isFocused && (
          <div
            className="absolute z-10 w-full bg-white shadow font-light overflow-auto"
            style={{
              maxHeight: 200,
              minWidth: 200,
            }}
          >
            {times?.map(t => (
              <div
                key={t.date.getTime()}
                id={`time-${t.date.getTime()}`}
                onMouseEnter={() => {
                  setHoveredTime(t)
                }}
                onMouseLeave={() => {
                  setHoveredTime(currentTime)
                }}
                className={[
                  'px-3 py-2 cursor-pointer',
                  'hover:bg-gray-200',
                  hoveredTime === t && 'bg-gray-200',
                ]}
                onMouseDown={() => {
                  // reset the input time
                  // need to do this so that the input will show the value.label instead
                  setInputTime()
                  onChange(t.date)
                }}
              >
                {t.label}
                {t.difference && (
                  <span className="text-xs text-gray-700"> ({t.difference})</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
